/* @jsxImportSource react */
import { useQuery } from '@tanstack/react-query';

import { metrics } from '../api/client';
import { formatAge, formatBytes, formatTs } from '../lib/format';
import { Badge, EmptyState, ErrorState, Spinner } from '../components/ui';

/**
 * `/metrics` (W4): the P1 numbers in full — per-job success rate / duration
 * trend / size anomalies, RPO violators, mirror lag per bucket, bucket usage
 * with the 7-day verify board. The overview carries the strip; this page is
 * the "why" behind it.
 */
export function MetricsPage() {
  const query = useQuery({
    queryKey: ['metrics'],
    queryFn: ({ signal }) => metrics.get(signal),
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Метрики</h1>
          <p className="page-header__sub">
            Окно 7 дней: success rate, длительность и размер против медианы, RPO-комплаенс, лаг зеркала, занятость бакетов, restore-verify.
          </p>
        </div>
      </div>

      {query.isPending ? <Spinner label="Считаю метрики" /> : null}
      {query.isError ? <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} /> : null}
      {query.data ? <MetricsBody data={query.data} /> : null}
    </>
  );
}

export function MetricsBody({ data }: { data: Awaited<ReturnType<typeof metrics.get>> }) {
  const rpo = data.rpo_compliance;
  return (
    <>
      <section className="card card--flush">
        <div className="header-strip" data-testid="metrics-summary">
          <span className="header-strip__label">
            success 7d: <strong>{data.success_rate_7d.pct === null ? '—' : `${data.success_rate_7d.pct.toFixed(0)}%`}</strong>
            <span className="muted"> ({data.success_rate_7d.ok}/{data.success_rate_7d.total})</span>
          </span>
          <span className="header-strip__label">
            RPO compliance: <strong>{rpo.pct === null ? '—' : `${rpo.pct.toFixed(0)}%`}</strong>
            <span className="muted"> ({rpo.within}/{rpo.total})</span>
          </span>
          <span className="header-strip__label muted">обновлено {formatTs(data.generated_at)}</span>
        </div>
      </section>

      <section className="card card--flush">
        <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
          По джобам ({data.jobs.length})
        </h2>
        <JobMetricsTable jobs={data.jobs} />
      </section>

      <section className="card">
        <h2 className="section-title">Аномалии</h2>
        <AnomalyLists jobs={data.jobs} warnings={data.warnings} />
      </section>

      {rpo.violators.length > 0 ? (
        <section className="card card--flush">
          <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
            Вне RPO ({rpo.violators.length})
          </h2>
          <div className="table-wrap">
            <table className="data" data-testid="rpo-violators">
              <thead>
                <tr>
                  <th>Джоба</th>
                  <th>Таргет</th>
                  <th className="num">Возраст</th>
                  <th className="num">RPO</th>
                </tr>
              </thead>
              <tbody>
                {rpo.violators.map((entry) => (
                  <tr key={`${entry.job}/${entry.target}`}>
                    <td>{entry.job}</td>
                    <td>{entry.target}</td>
                    <td className="num cell-muted">{formatAge(Math.round(entry.ageHours * 3600))}</td>
                    <td className="num cell-muted">{entry.rpoHours} ч</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="card card--flush">
        <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
          Лаг зеркала RU→KZ
        </h2>
        <MirrorLagTable lag={data.mirror_lag} />
      </section>

      <section className="card">
        <h2 className="section-title">Restore-verify ({data.restore_verify.job})</h2>
        <VerifyDayTable board={data.restore_verify.board} missed={data.restore_verify.missedDays} />
      </section>
    </>
  );
}

function JobMetricsTable({ jobs }: { jobs: Array<Awaited<ReturnType<typeof metrics.get>>['jobs'][number]> }) {
  if (jobs.length === 0) {
    return <EmptyState title="Прогонов в окне нет" hint="Метрики появятся после первых событий коллектора." />;
  }
  return (
    <div className="table-wrap">
      <table className="data" data-testid="job-metrics">
        <thead>
          <tr>
            <th>Джоба</th>
            <th className="num">Success 7d</th>
            <th className="num">Длит. (последняя)</th>
            <th className="num">Медиана 7d</th>
            <th className="num">Прогонов</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.job}>
              <td className="cell-primary">{job.job}</td>
              <td className="num">
                {job.successRate7d.pct === null ? (
                  <span className="cell-muted">—</span>
                ) : (
                  <>
                    {job.successRate7d.pct.toFixed(0)}% <span className="cell-muted">({job.successRate7d.ok}/{job.successRate7d.total})</span>
                  </>
                )}
              </td>
              <td className="num">
                {job.duration.lastS === null ? (
                  <span className="cell-muted">—</span>
                ) : (
                  <>
                    {formatAge(job.duration.lastS)}
                    {job.duration.anomalous ? (
                      <Badge tone="warn" title="> 2× медианы 7d">
                        slow
                      </Badge>
                    ) : null}
                  </>
                )}
              </td>
              <td className="num cell-muted">{formatAge(job.duration.medianS)}</td>
              <td className="num cell-muted">{job.duration.samples}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnomalyLists({ jobs, warnings }: { jobs: Array<Awaited<ReturnType<typeof metrics.get>>['jobs'][number]>; warnings: string[] }) {
  const sizes = jobs.flatMap((job) => job.sizeAnomalies.map((anomaly) => ({ job: job.job, ...anomaly })));
  const zeros = jobs.flatMap((job) => job.sizeZero.map((target) => ({ job: job.job, target })));
  if (sizes.length === 0 && zeros.length === 0) {
    return <EmptyState title="Аномалий нет" hint="Size < 0.5× медианы 7d (≥3 замеров) и duration > 2× медианы показались бы здесь." />;
  }
  return (
    <div data-testid="anomaly-lists">
      {zeros.map((entry) => (
        <div key={`${entry.job}/${entry.target}`} className="banner banner--bad">
          <strong>{entry.job}/{entry.target}</strong>
          <span>новейший артефакт 0 байт</span>
        </div>
      ))}
      {sizes.map((entry) => (
        <div key={`${entry.job}/${entry.target}`} className="banner banner--warn">
          <strong>{entry.job}/{entry.target}</strong>
          <span>
            {formatBytes(entry.lastBytes)} против медианы {formatBytes(entry.medianBytes)} ({entry.samples} замеров)
          </span>
        </div>
      ))}
      {warnings.length > 0 ? (
        <p className="field__hint">Строки для digest: {warnings.length}.</p>
      ) : null}
    </div>
  );
}

function MirrorLagTable({ lag }: { lag: Array<{ bucket: string; kzLagHours: number | null; kzEmpty: boolean; state: string }> }) {
  if (lag.length === 0) {
    return <EmptyState title="Синк ещё не считался" hint="Лаг появится после первого зеркального окна." />;
  }
  return (
    <div className="table-wrap">
      <table className="data" data-testid="mirror-lag">
        <thead>
          <tr>
            <th>Бакет</th>
            <th>Состояние</th>
            <th className="num">Лаг KZ</th>
          </tr>
        </thead>
        <tbody>
          {lag.map((entry) => (
            <tr key={entry.bucket}>
              <td className="cell-primary">{entry.bucket}</td>
              <td>{entry.state}</td>
              <td className="num cell-muted">{entry.kzEmpty ? 'KZ пуст' : entry.kzLagHours === null ? '—' : `${entry.kzLagHours.toFixed(1)} ч`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VerifyDayTable({ board, missed }: { board: Array<Awaited<ReturnType<typeof metrics.get>>['restore_verify']['board'][number]>; missed: number }) {
  if (board.length === 0) {
    return <EmptyState title="Verify-прогонов нет" hint="mongo-verify ещё не доставлял событий в коллектор." />;
  }
  return (
    <div className="table-wrap">
      <table className="data" data-testid="verify-days">
        <thead>
          <tr>
            <th>День (UTC)</th>
            <th>Вердикт</th>
            <th className="num">Прогонов</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {board.map((day) => (
            <tr key={day.day}>
              <td className="cell-primary nowrap">{day.day}</td>
              <td>
                {day.runs === 0 ? (
                  <Badge tone="warn" title="нет прогона в этот день">
                    miss
                  </Badge>
                ) : day.fail > 0 ? (
                  <Badge tone="bad" title={`${day.fail} fail`}>
                    fail
                  </Badge>
                ) : (
                  <Badge tone="ok" title={`${day.ok} ok`}>
                    ok
                  </Badge>
                )}
              </td>
              <td className="num cell-muted">{day.runs}</td>
              <td className="cell-muted">{day.lastNote ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {missed > 0 ? <p className="field__hint">Дней без прогона за окно: {missed}.</p> : null}
    </div>
  );
}
